import { api } from '../lib/api'
import type { ClientInsight, InsightProposal, InsightWeight, KnowledgeLevel, ManagerQuestion } from '../lib/types'

export interface ClientKnowledge extends KnowledgeLevel {
  id: string
  name: string
  managerId: string
  managerName: string
  curatedAt: string | null
}

/** Pergunta que já saiu da caixa: respondida ou "não se aplica" (com o que virou). */
export interface AnsweredQuestion {
  id: string
  clientId: string
  clientName: string
  question: string
  kind: 'choice' | 'yesno' | 'number' | 'text'
  options: string[]
  topic: string
  metric: string | null
  metricLabel: string | null
  metricBetterWhen: 'higher' | 'lower' | null
  campaign: string | null
  status: 'answered' | 'dismissed'
  answer: string | null
  answerNote: string | null
  answeredBy: string | null
  answeredAt: string
  targetValue: number | null // valor ATUAL da meta do KPI (topic=meta)
  facts: Array<{ id: string; content: string; active: boolean; status: 'proposed' | 'confirmed' | 'rejected' }>
}

/** Aprendizado que o gerente rejeitou ("Está errado"). */
export interface RejectedInsight {
  id: string
  clientId: string
  clientName: string
  content: string
  category: string
  weight: InsightWeight
  source: string
  evidence: string | null
  reviewedBy: string | null
  reviewedAt: string
}

export interface CurationResult {
  clientId: string
  ran: boolean
  reason: string
  facts: number
  questions: number
  signals: number
}

/** Caixa da IA: perguntas objetivas + fatos que o curador propôs (1 clique cada). */
export const intelligenceApi = {
  inbox: (clientId?: string) =>
    api.get<{ questions: ManagerQuestion[]; proposals: InsightProposal[] }>(
      `/intelligence/inbox${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`
    ),
  answer: (questionId: string, answer: string, note?: string) =>
    api.post<{ ok: true; insightId: string | null; targetMetric: string | null }>(
      `/intelligence/questions/${questionId}/answer`,
      { answer, note: note || null }
    ),
  dismiss: (questionId: string) => api.post<{ ok: true }>(`/intelligence/questions/${questionId}/dismiss`, {}),
  // Corrige uma resposta já dada (ou responde uma descartada): fato e meta são refeitos.
  correct: (questionId: string, answer: string, note?: string) =>
    api.post<{ ok: true; insightId: string | null; targetMetric: string | null }>(
      `/intelligence/questions/${questionId}/correct`,
      { answer, note: note || null }
    ),
  history: (clientId?: string) =>
    api.get<{ questions: AnsweredQuestion[]; rejected: RejectedInsight[] }>(
      `/intelligence/history${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`
    ),
  confirm: (insightId: string, edits: { content?: string; category?: string; weight?: InsightWeight } = {}) =>
    api.post<{ insight: ClientInsight }>(`/intelligence/insights/${insightId}/confirm`, edits),
  reject: (insightId: string) => api.post<{ insight: ClientInsight }>(`/intelligence/insights/${insightId}/reject`, {}),
  knowledge: (clientId?: string) =>
    api.get<{ clients: ClientKnowledge[] }>(
      `/intelligence/knowledge${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`
    ),
  curate: (clientId: string) => api.post<CurationResult>(`/intelligence/curate/${clientId}`, {}),
  curateAll: () => api.post<{ ok: true; started: true }>('/intelligence/curate-all', {}),
  site: (clientId: string) => api.get<ClientSiteView>(`/intelligence/site/${clientId}`),
  refreshSite: (clientId: string) =>
    api.post<ClientSiteView & { result: { ran: boolean; reason: string; facts: number } | null }>(
      `/intelligence/site/${clientId}/refresh`,
      {}
    ),
}

export interface SitePageView {
  id: string
  url: string
  spend30d: number
  clicks30d: number
  conv30d: number
  traffic: Array<{ campanha: string; grupo: string | null; gasto: number }> | null
  httpStatus: number | null
  finalUrl: string | null
  via: 'direct' | 'reader' | 'blocked' | null
  title: string | null
  signals: { whatsapp: boolean; formulario: boolean | null; telefone: boolean; precos: string[] } | null
  summary: string | null
  problem: string | null
  problemSince: string | null
  changedAt: string | null
  changeSummary: string | null
  checkedAt: string | null
  okAt: string | null
}

export interface ClientSiteView {
  siteUrl: string | null
  siteHome: string | null
  siteProfile: string | null
  siteProfileAt: string | null
  pages: SitePageView[]
}
